import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';

const RoomSetting = () => {
  const [rooms, setRooms] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ hostel_id: '', hostel_name: '', room_number: '', floor: 1, capacity: 1, facilities: '' });

  useEffect(() => { fetchRooms(); fetchHostels(); }, []);

  const fetchRooms = async () => {
    try { 
      const response = await apiClient.get('/api/hostel/rooms'); 
      setRooms(Array.isArray(response.data) ? response.data : []); 
    } catch (error) { 
      console.error('Error:', error); 
    }
  };

  const fetchHostels = async () => {
    try { 
      const response = await apiClient.get('/api/hostel/hostels'); 
      setHostels(Array.isArray(response.data) ? response.data : []); 
    } catch (error) { 
      console.error('Error:', error); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = editingRoom ? `/api/hostel/rooms/${editingRoom.id}` : `/api/hostel/rooms`;
      const payload = { 
        ...formData, 
        current_occupancy: editingRoom ? editingRoom.current_occupancy : 0, 
        status: editingRoom ? editingRoom.status : 'Available' 
      };
      
      const response = editingRoom 
        ? await apiClient.put(url, payload) 
        : await apiClient.post(url, payload);
        
      if (response.status === 200 || response.status === 201 || response.data) { 
        alert('Room saved successfully!'); 
        fetchRooms(); 
        handleCloseModal(); 
      }
    } catch (error) { 
      console.error('Error saving room:', error);
      let errorMsg = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMsg = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      alert('Failed to save room: ' + errorMsg); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this room?')) { 
      try {
        await apiClient.delete(`/api/hostel/rooms/${id}`); 
        fetchRooms(); 
        alert('Room deleted successfully!');
      } catch (error) {
        alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setFormData({ 
      hostel_id: room.hostel_id, 
      hostel_name: room.hostel_name, 
      room_number: room.room_number, 
      floor: room.floor, 
      capacity: room.capacity, 
      facilities: room.facilities || '' 
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => { 
    setIsModalOpen(false); 
    setEditingRoom(null); 
    setFormData({ hostel_id: '', hostel_name: '', room_number: '', floor: 1, capacity: 1, facilities: '' }); 
  };

  const handleHostelChange = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    if (hostel) setFormData({ ...formData, hostel_id: hostelId, hostel_name: hostel.hostel_name });
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Room Settings</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}><Plus className="w-4 h-4 mr-2" />Add Room</Button>
        </CardHeader>
        <CardContent>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">Hostel</th>
                <th className="text-left p-3">Room #</th>
                <th className="text-left p-3">Floor</th>
                <th className="text-left p-3">Occupancy</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms.length === 0 ? (
                <tr><td colSpan="6" className="text-center p-4 text-gray-500">No rooms found.</td></tr>
              ) : (
                rooms.map((room) => (
                  <tr key={room.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{room.hostel_name}</td>
                    <td className="p-3 font-medium">{room.room_number}</td>
                    <td className="p-3">Floor {room.floor}</td>
                    <td className="p-3">{room.current_occupancy}/{room.capacity}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        room.status === 'Available' ? 'bg-green-100 text-green-800' : 
                        room.status === 'Occupied' ? 'bg-blue-100 text-blue-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {room.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(room)}><Edit className="w-3 h-3" /></Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(room.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingRoom ? 'Edit' : 'Add'} Room</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hostel *</Label>
                <Select value={formData.hostel_id} onValueChange={handleHostelChange}>
                  <SelectTrigger><SelectValue placeholder="Select hostel" /></SelectTrigger>
                  <SelectContent>
                    {hostels.map((h) => <SelectItem key={h.id} value={h.id}>{h.hostel_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Room Number *</Label>
                <Input required value={formData.room_number} onChange={(e) => setFormData({ ...formData, room_number: e.target.value })} placeholder="e.g., 101" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Floor *</Label>
                <Input type="number" min="0" required value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Capacity *</Label>
                <Input type="number" min="1" required value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label>Facilities</Label>
              <Input value={formData.facilities} onChange={(e) => setFormData({ ...formData, facilities: e.target.value })} placeholder="AC, Attached Bath, etc." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomSetting;
